// v5.8 A20 — Terminology overrides consumer.
// Reads `tenant.terminology` (loaded by AuthProvider via /auth/me) and returns a
// translation function `t(term)` that maps canonical CompeteWiser words to the
// tenant's preferred labels while preserving casing when the exact form isn't
// in the map.
//
// Usage:
//   import { useTerm } from "@/lib/terminology";
//   const t = useTerm();
//   <button>New {t("Deal")}</button>
//
// The mapping is defined by the VC Admin under Settings → Terminology.
// Example map: { "Deal": "Startup", "Founder": "Entrepreneur" }.
import { useCallback } from "react";
import { useAuth } from "@/lib/auth.jsx";

function applyCasing(source, target) {
  if (!source) return target;
  if (source === source.toUpperCase()) return target.toUpperCase();
  if (source[0] === source[0].toUpperCase()) {
    return target[0].toUpperCase() + target.slice(1);
  }
  return target.toLowerCase();
}

// Very small pluralizer covering the terms we ship as suggestions.
// Only used when a singular is mapped but caller passes plural (e.g. map has
// "Deal": "Startup" and caller writes t("Deals")).
function pluralize(word) {
  if (!word) return word;
  const lower = word.toLowerCase();
  if (lower.endsWith("s")) return word;
  if (lower.endsWith("y") && !/[aeiou]y$/i.test(word)) return word.slice(0, -1) + "ies";
  if (/(s|x|z|ch|sh)$/i.test(word)) return word + "es";
  return word + "s";
}
function singularize(word) {
  if (!word) return word;
  const lower = word.toLowerCase();
  if (lower.endsWith("ies")) return word.slice(0, -3) + "y";
  if (lower.endsWith("es") && /(s|x|z|ch|sh)es$/i.test(word)) return word.slice(0, -2);
  if (lower.endsWith("s") && !lower.endsWith("ss")) return word.slice(0, -1);
  return word;
}

export function useTerm() {
  const { tenant } = useAuth();
  const map = tenant?.terminology || {};

  return useCallback(
    (term) => {
      if (!term || typeof term !== "string") return term;

      // 1) exact match
      if (map[term]) return map[term];

      // 2) case-insensitive match on the exact word
      const lc = term.toLowerCase();
      for (const [k, v] of Object.entries(map)) {
        if (k.toLowerCase() === lc) return applyCasing(term, v);
      }

      // 3) plural handling — caller asks for "Deals" but map has "Deal"
      const sing = singularize(term);
      if (sing !== term) {
        for (const [k, v] of Object.entries(map)) {
          if (k.toLowerCase() === sing.toLowerCase()) {
            return applyCasing(term, pluralize(v));
          }
        }
      }

      // 4) singular handling — caller asks for "Deal" but map has "Deals"
      const plur = pluralize(term);
      if (plur !== term) {
        for (const [k, v] of Object.entries(map)) {
          if (k.toLowerCase() === plur.toLowerCase()) {
            return applyCasing(term, singularize(v));
          }
        }
      }

      return term;
    },
    [map],
  );
}
